/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Link, styled, SxProps, Typography } from "@mui/material";
import React from "react";

import TextureTyreBrown from "../assets/images/TextureTyreBrown.png";
import Analytics from "../services/analytics";
import { TestIds } from "../testIds";
import { renderWithWordBreaks } from "../util/renderWithWordBreaks";

import YesNoAction from "./YesNoAction";

const ContactCardContainer = styled("div")(
  ({ theme }) => `
    max-width: 668px;
    min-width: 50%;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    background: ${theme.colors.secondary700};
  `
);

const InnerCardContainer = styled("div")(
  ({ theme }) => `
  min-width: 75%;
  display: flex;
  flex-direction: column;
  padding: 24px 32px;
  gap: 16px;
  ${[theme.breakpoints.down("md")]} {
    padding: 16px;
}
  `
);

const ImageContainer = styled("div")(
  ({ theme }) => `
  ({ theme }) =>display: flex;
  min-height: 100px;
  min-width: 100px;
  background-image: url(${TextureTyreBrown});
  background-size: cover;
  ${[theme.breakpoints.down("sm")]} {
    display: none;
}
`
);

export const ContactItem = styled(Link)<{ color?: string }>`
  text-decoration: none;
  color: ${({ theme, color }) => color ?? theme.colors.shade0};
  display: flex;
  gap: 8px;
  margin-right: 24px;
  overflow-wrap: anywhere;
  .material-symbols-outlined {
    color: ${({ theme, color }) => color ?? theme.colors.secondary100};
  }
`;

export const ContactContainer = styled("div")`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TitleContainer = styled("div")<{ titleGap: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ titleGap }) => `${titleGap}px`};
  z-index: 1;
`;

interface InnerCardProps {
  title: string;
  body?: string | React.ReactNode;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  titleGap?: number;
  storeName?: string;
}

interface ContactCardProps extends InnerCardProps {
  sx?: SxProps;
  testId?: string;
  className?: string;
}

export const InnerContactCard = ({
  title,
  name,
  email,
  body,
  phone,
  storeName,
  titleGap = 8,
}: InnerCardProps) => {
  const storeDirectionUrl = storeName
    ? `https://maps.google.com/maps?q=Farmlands+${storeName}`
    : undefined;

  function onClickEmail() {
    Analytics.push({
      dataLayer: {
        event: "representative_email",
      },
    });
  }

  function onClickPhone() {
    window.open(`tel:${phone}`, "_blank");
    Analytics.push({
      dataLayer: {
        event: "representative_phone",
      },
    });
  }

  function onClickStoreDirection() {
    window.open(storeDirectionUrl, "_blank");
  }

  const renderPhoneAndGetDirectionButtons = React.useMemo(() => {
    const getDirectionButtonProps = {
      label: "Get directions",
      startIcon: (
        <span className="material-symbols-fill" style={{ fontWeight: 400 }}>
          directions
        </span>
      ),
      testId: `${TestIds.components.contactCard.storeDirection}`,
      onClick: onClickStoreDirection,
    };

    if (phone && storeName) {
      return (
        <Box sx={{ mb: 1 }}>
          <YesNoAction
            optionA={{
              label: phone,
              startIcon: (
                <span className="material-symbols-fill" style={{ fontWeight: 400 }}>
                  call
                </span>
              ),
              testId: `${TestIds.components.contactCard.phone}`,
              onClick: onClickPhone,
            }}
            optionB={getDirectionButtonProps}
            color="tertiary"
          />
        </Box>
      );
    }

    if (!phone && storeName) {
      return <YesNoAction optionA={getDirectionButtonProps} color="tertiary" />;
    }
    if (phone && !storeName) {
      return (
        <ContactItem href={`tel:${phone}`} onClickCapture={onClickPhone}>
          <div className="material-symbols-outlined">phone</div>
          <Typography variant="body2" color="white" sx={{ textDecoration: "underline" }}>
            {phone}
          </Typography>
        </ContactItem>
      );
    }
  }, [phone, storeName]);

  return (
    <InnerCardContainer>
      <TitleContainer titleGap={titleGap} data-testid={TestIds.components.contactCard.title}>
        <Typography variant="h4" component={"p"} color="white">
          {title.split("\n").map((e, i) => (
            <span key={i}>
              {e}
              <br />
            </span>
          ))}
        </Typography>
        {typeof body === "object" && body}
        {name && (
          <Typography variant="body1" color="white">
            {name}
          </Typography>
        )}
      </TitleContainer>

      {(email ?? phone ?? storeName) && (
        <ContactContainer>
          {(phone ?? storeName) && (
            <ContactContainer>{renderPhoneAndGetDirectionButtons}</ContactContainer>
          )}

          {email && (
            <ContactContainer>
              <ContactItem href={`mailto:${email}`} onClickCapture={onClickEmail}>
                <div className="material-symbols-outlined">email</div>
                <Typography variant="body2" color="white" sx={{ textDecoration: "underline" }}>
                  {renderWithWordBreaks(email)}
                </Typography>
              </ContactItem>
            </ContactContainer>
          )}
        </ContactContainer>
      )}
    </InnerCardContainer>
  );
};

const ContactCard = ({
  title,
  name,
  email,
  body,
  phone,
  titleGap = 8,
  storeName,
  testId,
  ...rest
}: ContactCardProps) => {
  // 1. Your rep
  // 2. Your selected branch
  // 3. Your default branch contact details
  // 4. 0800 generic

  return (
    <ContactCardContainer data-testid={testId} {...rest}>
      <InnerContactCard
        title={title}
        name={name}
        email={email}
        phone={phone}
        body={body}
        titleGap={titleGap}
        storeName={storeName}
      />
      <ImageContainer />
    </ContactCardContainer>
  );
};

export default ContactCard;
